import React, { FC, useCallback, useEffect, useState } from "react";

import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { isNil } from "lodash";

import { Participant, ParticipantTypes } from "@api/graphql";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { TableContents, TableHeader } from "@components/table/TableContents";
import { TableHeaders as CustomTableHeaders } from "@components/table/TableHeaders";
import { formatDateStringLong, getParticipantTypesForDisplay } from "@util/index";

import styles from "./ParticipantTable.module.scss";

export enum ResaleStatusComponentType {
  BADGE = "Badge",
  DROPDOWN = "Dropdown",
}

const DEFAULT_TABLE_HEADERS: TableHeader<Participant>[] = [
  { label: "Account ID", accessor: "participant_id" },
  { label: "Account name", accessor: "full_name" },
  { label: "Account type", accessor: "participant_types" },
  { label: "Join date", accessor: "deceased" },
  { label: "Account status", accessor: "participant_status" },
];

interface ProvisionalResaleTableProps {
  participants: Participant[];
  onRowClick?: (rowData: Participant) => void;
  customTableHeaders?: TableHeader<Participant>[];
  customTableStyleClassName?: string;
}

const WithTooltip = ({ children, label }: { children: React.ReactElement; label: string }) => {
  return (
    <OverlayTrigger placement="top-start" overlay={<Tooltip>{label}</Tooltip>}>
      {children}
    </OverlayTrigger>
  );
};

export const ParticipantTable: FC<ProvisionalResaleTableProps> = ({
  participants,
  onRowClick,
  customTableHeaders,
  customTableStyleClassName,
}) => {
  const [sortKey, setSortKey] = useState<keyof Participant>("participant_status");
  const [sortDirection, setSortDirection] = useState<"ascending" | "descending">("ascending");
  const [sortedData, setSortedData] = useState(participants);

  const mapColumnValue = useCallback(
    (value: Participant[keyof Participant], key: keyof Participant, row?: Participant) => {
      // convert nullish value to "", Boolean("") => false
      const text = isNil(value) ? "" : value;

      // default component
      // TODO: [NRRS-1003] extract components and the whole if statement
      let component = (
        <td className="align-content-center" key={String(key)}>
          <span>{text}</span>
        </td>
      );
      if (row) {
        const { participant_id, full_name, participant_types, participant_status, company_name, date_created } = row;

        if (key === "participant_id") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <WithTooltip label={text.toString()}>
                <span>{text}</span>
              </WithTooltip>
            </td>
          );
        } else if (key === "full_name") {
          const isArtCentre = participant_types?.includes(ParticipantTypes.ART_CENTRE);
          component = (
            <td className="align-content-center" key={String(key)}>
              {/* Display company_name if type is "Art Centre", otherwise display full_name */}
              <ParticipantName
                name={isArtCentre ? company_name ?? full_name : full_name}
                participantId={participant_id}
                defaultTab={"AccountDetails"}
              />
            </td>
          );
        } else if (key === "participant_status") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div className={classNames(styles["badge-cell"], styles["badge-truncate"])}>
                <ParticipantStatusBadge status={participant_status} />
              </div>
            </td>
          );
        } else if (key === "participant_types") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{getParticipantTypesForDisplay(participant_types)}</div>
            </td>
          );
        } else if (key === "date_created") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{date_created ? formatDateStringLong(date_created.substring(0, 10)) : ""}</div>
            </td>
          );
        }
      }
      return { component };
    },
    [],
  );

  useEffect(() => {
    const sortedData = [...participants].sort((a, b) => {
      if (sortDirection === "ascending") {
        return a[sortKey]! > b[sortKey]! ? 1 : -1;
      }
      return a[sortKey]! < b[sortKey]! ? 1 : -1;
    });
    setSortedData(sortedData);
  }, [participants, sortDirection, sortKey]);

  const handleSort = (newSortKey: keyof Participant) => {
    if (sortKey === newSortKey) {
      setSortDirection((prevDirection) => (prevDirection === "ascending" ? "descending" : "ascending"));
    } else {
      setSortKey(newSortKey);
      setSortDirection("ascending");
    }
  };

  return (
    <>
      <Table borderless hover className={classNames(customTableStyleClassName, styles["custom-table"])}>
        <CustomTableHeaders
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          customStyles={styles}
          sortColumn={sortKey}
          sortDirection={sortDirection}
          setSortColumn={handleSort}
        />

        <TableContents<Participant>
          headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
          data={sortedData}
          keyAccessor="participant_id"
          customStyles={styles}
          mapColumnValue={mapColumnValue}
          onRowClick={onRowClick}
        />
      </Table>
    </>
  );
};
